
import React, { useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationsContext';
import { NotificationsList } from './NotificationsList';

export const NotificationsPortal = () => {
  const { showNotifications, setShowNotifications } = useNotifications();
  const notificationsRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the notifications panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationsRef.current && 
        !notificationsRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('[data-notifications-toggle]')
      ) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, setShowNotifications]);

  // Handle escape key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showNotifications, setShowNotifications]);

  if (!showNotifications) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-start pointer-events-none">
      <div className="w-full md:w-96 mt-16 mr-4 md:mr-8 pointer-events-auto self-start ml-auto">
        <div 
          ref={notificationsRef}
          className="bg-background shadow-lg border rounded-md overflow-hidden"
        >
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold">Notifications</h3>
            <button 
              onClick={() => setShowNotifications(false)}
              className="h-6 w-6 rounded-full bg-gray-200/50 hover:bg-gray-200/80 flex items-center justify-center"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <NotificationsList />
        </div>
      </div>
    </div>
  );
};
